import React, { Dispatch, useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import { isProduct, ProductId } from "multitenancy/product";

import {
  queueCreate,
  cancelCreate,
  queueDelete,
  cancelDelete,
} from "store/modules/profilePermissionQueue/actions/actions.ts";

import { colors } from "global/globalStyle";

import { useLoadingProfileContext } from "pages/app/Profiles/profile-hook";
import {
  ContainerFormOneLabelSwitch,
  ContainerLabelSwitch,
  Drawer,
} from "pages/app/StyleCSS/bodyCreateEdit";

import Dropdown from "components/Dropdown";
import { DropdownGlobal, IDropDown } from "components/DropdownGlobal";
import GeneralInput from "components/GeneralInput";
import Switch from "components/Switch";

import { profilesService } from "services/profiles";

import { Container } from "./styles";

type IProfileBodyModal = {
  active: boolean;
  onChangeSwitch: (active: boolean) => unknown;
  name?: any;
  description?: any;
  formsListData?: any;
  nameError: boolean;
  descriptionError: boolean;
  setName: React.Dispatch<React.SetStateAction<string>>;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  isEdit: boolean;
  userCustomerProfile?: number;
  adminSwitchValue?: any;
  onAdminSwitchChange?: any;
  confidentialityLevel: number | string;
  setConfidentialityLevel: React.Dispatch<React.SetStateAction<number>>;
};

type PendingCreateItem = {
  optionId: number | string;
  actionId: number | string;
  soUserCustomerProfileId?: number | null;
};

type PendingDeleteItem = {
  optionId: number | string;
  actionId: number | string;
  soUserCustomerProfileActionId?: number | string | null;
  systemObjectUserCustomerProfileActionId?: number | string | null;
};

interface IFormAction {
  systemActionId: number;
  soUserCustomerProfileActionId: any;
  action: string;
  active: boolean;
}

interface IForm {
  userCustomerProfileId: any;
  systemObjectCustomerId: number;
  soUserCustomerProfileId?: any;
  systemObjectUserCustomerProfileId?: any;
  name?: string;
  description: string;
  actionsList: IFormAction[];
}

interface IIdPair {
  optionId: string | number;
  actionId: string | number;
  soUserCustomerProfileActionId?: string | number | null;
  systemObjectUserCustomerProfileActionId?: string | number | null;
  soUserCustomerProfileId?: string | number;
  active?: boolean;
}

const secrecyLevelIds = [0, 1, 2, 3, 4, 5];

const ProfileBodyModal: React.FC<IProfileBodyModal> = ({
  active,
  onChangeSwitch,
  name,
  description,
  setName,
  setDescription,
  nameError,
  descriptionError,
  isEdit,
  userCustomerProfile,
  adminSwitchValue,
  onAdminSwitchChange,
  confidentialityLevel,
  setConfidentialityLevel,
}) => {
  const { t } = useTranslation();
  const { userCreateProfile } = useSelector(
    (state: any) => state.userCreateProfile,
  );
  const [options, setOptions] = useState<any[]>([]);
  const [formListAction, setFormListAction] = useState<IIdPair[]>([]);
  const [formListReport, setFormListReport] = useState<IIdPair[]>([]);
  const [errorNameRequired, setErrorNameRequired] = useState(nameError);
  const [loading, setLoading] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [isSecrecyLevelInfoOpen, setIsSecrecyLevelInfoOpen] = useState(false);
  const [errorDescriptionRequired, setErrorDescriptionRequired] =
    useState(descriptionError);
  const { userCreateUnit } = useSelector(
    (state: any) => state.userCreateDistrict,
  );
  const { loadingProfile } = useLoadingProfileContext();
  const showSecrecyLevel =
    !isProduct(ProductId.Inquerito) && !isProduct(ProductId.Cartorio);
  const dispatch = useDispatch();
  const { pendingCreates, pendingDeletes } = useSelector(
    (state: any) => state.profilePermissionQueue,
  );
  const secrecyLevelOptions: IDropDown[] = secrecyLevelIds.map((id) => ({
    id,
    name: t(`profiles.secrecyLevels.${id}.option`),
  }));
  const selectedSecrecyLevelOption =
    secrecyLevelOptions.find(
      (option) => Number(option.id) === Number(confidentialityLevel),
    ) || secrecyLevelOptions[0];

  async function getData() {
    setLoadingUpdate(true);
    const response = await profilesService.getFormList(
      userCreateProfile?.participantItem?.id
        ? userCreateProfile?.participantItem?.id
        : 0,
    );

    if (response?.body?.formsList) {
      const dataFormatted: any[] = response?.body?.formsList?.map(
        (item: IForm) => {
          return {
            title: item.name
              ? t(`systemObjects.${item.name}`, {
                  defaultValue: item.description,
                })
              : item.description,
            id: item.systemObjectCustomerId,
            userCustomerProfileId: item.userCustomerProfileId,
            systemObjectCustomerId: item.systemObjectCustomerId,
            soUserCustomerProfileId:
              item.soUserCustomerProfileId ||
              item.systemObjectUserCustomerProfileId,
            systemObjectUserCustomerProfileId:
              item.systemObjectUserCustomerProfileId,
            actionsList: item.actionsList.map((act: IFormAction) => ({
              ...act,
              action: t(`systemActions.${act.action}`, {
                defaultValue: act.action,
              }),
            })),
          };
        },
      );
      setOptions(dataFormatted);
      let dataProfile = {
        name: name,
        description: description,
      };
      localStorage.setItem("dataProfile", JSON.stringify(dataProfile));

      setOptions(dataFormatted);

      dataProfile = {
        name: "",
        description: "",
      };

      localStorage.setItem("dataProfile", JSON.stringify(dataProfile));
    }

    setLoadingUpdate(false);
  }

  const handleOptionClick = (
    idPair: IIdPair,
    optionList: any[],
    setOptionList: Dispatch<any>,
  ) => {
    const isSamePair = (a: any, b: any) =>
      Number(a.optionId) === Number(b.optionId) &&
      Number(a.actionId) === Number(b.actionId);

    const isInCreates = pendingCreates.some((x: any) => isSamePair(x, idPair));
    const isInDeletes = pendingDeletes.some((x: any) => isSamePair(x, idPair));

    const initialActive = Boolean(
      (idPair as any).systemObjectUserCustomerProfileActionId ??
      (idPair as any).soUserCustomerProfileActionId ??
      null,
    );

    const effectiveActive = initialActive ? !isInDeletes : isInCreates;
    const targetActive = !effectiveActive;
    if (targetActive) {
      if (initialActive) {
        dispatch(
          cancelDelete({
            optionId: idPair.optionId,
            actionId: idPair.actionId,
          }),
        );
      } else {
        dispatch(
          queueCreate({
            optionId: idPair.optionId,
            actionId: idPair.actionId,
            soUserCustomerProfileId: (idPair.soUserCustomerProfileId ??
              null) as any,
          }),
        );
      }
    } else {
      if (initialActive) {
        dispatch(
          queueDelete({
            optionId: idPair.optionId,
            actionId: idPair.actionId,
            soUserCustomerProfileActionId:
              (idPair.soUserCustomerProfileActionId ?? null) as any,
            systemObjectUserCustomerProfileActionId:
              (idPair.systemObjectUserCustomerProfileActionId ?? null) as any,
          }),
        );
      } else {
        dispatch(
          cancelCreate({
            optionId: idPair.optionId,
            actionId: idPair.actionId,
          }),
        );
      }
    }
    const existsInOptionList = optionList.some((o) => isSamePair(o, idPair));
    const nextList = targetActive
      ? existsInOptionList
        ? optionList
        : [...optionList, idPair]
      : optionList.filter((o) => !isSamePair(o, idPair));
    setOptionList(nextList);
  };

  useEffect(() => {
    setErrorNameRequired(nameError);
    setErrorDescriptionRequired(descriptionError);
  }, [nameError, descriptionError]);

  useEffect(() => {
    if (userCreateUnit?.errorName) {
      setErrorNameRequired(true);
    } else {
      setErrorNameRequired(false);
    }
    if (userCreateUnit?.errorEmail) {
      setErrorDescriptionRequired(true);
    } else {
      setErrorDescriptionRequired(false);
    }
  }, [userCreateUnit]);

  useEffect(() => {
    if (userCreateProfile?.participantItem) {
      setName(userCreateProfile?.participantItem?.name);
      setDescription(userCreateProfile?.participantItem?.description);
    }

    (async () => {
      setLoading(true);
      await getData();
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const createList = pendingCreates as PendingCreateItem[];
    const deleteList = pendingDeletes as PendingDeleteItem[];

    const hasCreate = (optId: number | string, actId: number | string) =>
      createList.some(
        (x: PendingCreateItem) =>
          Number(x.optionId) === Number(optId) &&
          Number(x.actionId) === Number(actId),
      );

    const hasDelete = (optId: number | string, actId: number | string) =>
      deleteList.some(
        (x: PendingDeleteItem) =>
          Number(x.optionId) === Number(optId) &&
          Number(x.actionId) === Number(actId),
      );

    let changed = false;

    const remapped = options.map((opt: any) => {
      const actionsList = (opt.actionsList || []).map((act: any) => {
        const existingActionId =
          act.systemObjectUserCustomerProfileActionId ??
          act.soUserCustomerProfileActionId ??
          null;

        const initialActive = existingActionId != null || act.active === true;

        const effectiveActive = initialActive
          ? !hasDelete(opt.id, act.systemActionId)
          : hasCreate(opt.id, act.systemActionId);

        const desiredSoId = effectiveActive ? existingActionId : null;

        if (
          act.active !== effectiveActive ||
          (act.soUserCustomerProfileActionId ?? null) !== desiredSoId ||
          (act.systemObjectUserCustomerProfileActionId ?? null) !== desiredSoId
        ) {
          changed = true;
        }

        return {
          ...act,
          active: effectiveActive,
          soUserCustomerProfileActionId: desiredSoId,
          systemObjectUserCustomerProfileActionId: desiredSoId,
        };
      });

      return { ...opt, actionsList };
    });

    if (changed) {
      setOptions(remapped);
    }
  }, [options, formListAction, formListReport, pendingCreates, pendingDeletes]);
  return (
    <Container disableInteractions={loadingProfile}>
      <ContainerFormOneLabelSwitch>
        <Switch
          dataTest="switch_active"
          checked={active}
          onChange={() => onChangeSwitch(!active)}
          label={t("common.active")}
        />
      </ContainerFormOneLabelSwitch>
      <GeneralInput
        name={"Nome"}
        label={t("common.name")}
        value={name}
        error={errorNameRequired}
        setError={setErrorNameRequired}
        autofocus={true}
        maxLength={24}
        required={true}
        onChange={(e: string) => setName(e)}
      />

      <div style={{ marginTop: 16 }}>
        <GeneralInput
          name={"Descricao"}
          label={t("common.description")}
          value={description}
          error={errorDescriptionRequired}
          setError={setErrorDescriptionRequired}
          autofocus={false}
          maxLength={45}
          required={true}
          onChange={(e: string) => setDescription(e)}
        />
      </div>

      <ContainerLabelSwitch>
        <Switch
          dataTest="switch_admin"
          label={t("profiles.administrator")}
          checked={!!adminSwitchValue}
          onChange={() => onAdminSwitchChange(!adminSwitchValue)}
        />
      </ContainerLabelSwitch>

      {showSecrecyLevel && (
        <DropdownGlobal
          key={`profile_secrecy_level_${confidentialityLevel}`}
          id="profile_secrecy_level"
          width="100%"
          labelName={
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              {t("profiles.secrecyLevel")}
              <Tooltip title={t("profiles.secrecyLevelInfoTooltip")}>
                <IconButton
                  aria-label={t("profiles.secrecyLevelInfoAriaLabel")}
                  size="small"
                  sx={{ ml: 0.5, p: 0.25 }}
                  onClick={() => setIsSecrecyLevelInfoOpen(true)}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </span>
          }
          options={secrecyLevelOptions}
          editDropdown={selectedSecrecyLevelOption}
          defaultValue={t("profiles.secrecyLevels.0.option")}
          selected={confidentialityLevel !== ""}
          setSelected={(value: number | string) => {
            const nextLevel = Number(value);
            if (Number.isFinite(nextLevel)) {
              setConfidentialityLevel(nextLevel);
            }
          }}
        />
      )}

      <Drawer style={{ marginTop: 32 }} />

      <Dropdown
        title={t("common.actions")}
        loadingUpdate={loadingUpdate}
        options={options}
        loading={loading}
        onChange={(value: IIdPair) => {
          handleOptionClick(value, formListAction, setFormListAction);
        }}
        isCreateNewProfile={!isEdit}
      />

      <Dialog
        open={showSecrecyLevel && isSecrecyLevelInfoOpen}
        onClose={() => setIsSecrecyLevelInfoOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.background_primary,
            color: colors.default_base_white,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography fontSize={20} fontWeight={700} component="span">
            {t("profiles.secrecyLevel")}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setIsSecrecyLevelInfoOpen(false)}
          >
            <CloseIcon sx={{ color: colors.general_icons }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <TableContainer>
            <Table
              size="small"
              aria-label={t("profiles.secrecyLevelTableAriaLabel")}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("profiles.secrecyLevelColumns.level")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("profiles.secrecyLevelColumns.classification")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("profiles.secrecyLevelColumns.access")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {secrecyLevelIds.map((level) => (
                  <TableRow key={level}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {t(`profiles.secrecyLevels.${level}.level`)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {t(`profiles.secrecyLevels.${level}.classification`)}
                    </TableCell>
                    <TableCell>
                      {t(`profiles.secrecyLevels.${level}.access`)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ProfileBodyModal;
